import React from "react";
import { BLADE_COMPONENTS } from "@riot-private/blades-ui";
import type { BladeType } from "@riot-private/blades-ui";
import { LooseBlade } from "@riot-private/blades-ui/blades-api-client";
import { parseBladeData } from "../utils/api/validate";
import { reportSiteBuilderBladeError } from "../utils/renderer/createSiteBuilderBladeRegistry";
import { ErrorBlade } from "./ErrorBlade";

type RenderBladeProps = LooseBlade & {
  index: number;
  locale: string;
  environment: string;
  showBladeDiagnostics?: boolean;
};

// `BLADE_COMPONENTS` is blades-ui's map of every blade type literal to its component, or to `null`
// for literals that library ships nothing for. It replaces the switch and the `assertNever` check
// that used to live here.
//
// Do not reintroduce a type-level exhaustive check in this repository. Upstream already enforces
// one — `Record<BladeType, ...>` fails the blades-ui build the moment a literal is added to
// blades-schemas — and a second copy here is what made every dependency bump a breaking change.
// The runtime report below plus tests/unit/blade-registry.test.ts cover this side.
function isMappedBladeType(type: string): type is BladeType {
  return Object.prototype.hasOwnProperty.call(BLADE_COMPONENTS, type);
}

export function RenderBlade({
  index,
  showBladeDiagnostics = false,
  ...bladeProps
}: RenderBladeProps) {
  if (!isMappedBladeType(bladeProps.type)) {
    // A type blades-ui has never heard of: PCS is emitting something this build cannot render.
    // Report it and render nothing rather than breaking the page.
    reportSiteBuilderBladeError(undefined, {
      phase: "validation",
      index,
      bladeType: bladeProps.type,
    });
    return showBladeDiagnostics ? (
      <ErrorBlade
        phase="validation"
        index={index}
        bladeType={bladeProps.type}
      />
    ) : null;
  }

  const Component = BLADE_COMPONENTS[bladeProps.type];

  // A `null` entry is a type blades-ui deliberately does not render. Skipped before validation, so
  // upstream contract changes to blades we already ignore cannot surface an error blade — and
  // silently, because this is expected rather than a fault.
  if (!Component) {
    return null;
  }

  try {
    const { type: _type, ...bladeData } = parseBladeData(bladeProps, index);
    return <Component {...bladeData} />;
  } catch (error) {
    reportSiteBuilderBladeError(error, {
      phase: "validation",
      index,
      bladeType: bladeProps.type,
    });
    return showBladeDiagnostics ? (
      <ErrorBlade
        phase="validation"
        index={index}
        bladeType={bladeProps.type}
      />
    ) : null;
  }
}
